import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  HiPlus as Add,
  HiTrendingUp as Analytics,
  HiCheckCircle as CheckCircle,
  HiExclamation as Conflicts,
  HiTrash as Delete,
  HiPencil as Edit,
  HiChevronDown as ExpandMore,
  HiExclamationCircle as Hard,
  HiHeart as Heart,
  HiAdjustments as Preferences,
  HiClipboard as Rules,
  HiCog as Settings,
  HiInformationCircle as Soft,
  HiStar as Star,
  HiEye as Visibility,
  HiExclamation as Warning,
} from 'react-icons/hi';
import { mockData } from './MockData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rules-tabpanel-${index}`}
      aria-labelledby={`rules-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TimetableRulesPreferences: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [newRule, setNewRule] = useState<any>({});
  const [rules, setRules] = useState(mockData.rules);
  const [conflicts] = useState(mockData.conflicts);
  const [weights, setWeights] = useState(mockData.preferenceWeights);
  const [profiles] = useState(mockData.weightingProfiles);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getRuleIcon = (type: string) => {
    switch (type) {
      case 'hard':
        return <Hard color="error" />;
      case 'soft':
        return <Soft color="warning" />;
      case 'preference':
        return <Heart color="info" />;
      case 'teaching-preference':
        return <Star color="primary" />;
      default:
        return <Rules />;
    }
  };

  const getRuleColor = (type: string) => {
    switch (type) {
      case 'hard':
        return 'error';
      case 'soft':
        return 'warning';
      case 'preference':
        return 'info';
      case 'teaching-preference':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getConflictColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'major':
        return 'warning';
      case 'minor':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleAddRule = () => {
    const ruleWithId = { ...newRule, id: Date.now().toString() };
    setRules([...rules, ruleWithId]);
    setNewRule({});
    setShowRuleDialog(false);
  };

  const handleWeightChange = (weightId: string, newValue: number) => {
    setWeights(weights.map((w) => (w.id === weightId ? { ...w, currentWeight: newValue } : w)));
  };

  const renderRuleManagement = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Timetable Rules</Typography>
          <Button startIcon={<Add />} onClick={() => setShowRuleDialog(true)}>
            Add Rule
          </Button>
        </Box>

        <Grid container spacing={2}>
          {rules.map((rule) => (
            <Grid item xs={12} md={6} lg={4} key={rule.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {getRuleIcon(rule.type)}
                    <Box sx={{ ml: 1, flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {rule.name}
                      </Typography>
                      <Chip label={rule.type} color={getRuleColor(rule.type) as any} size="small" sx={{ mt: 0.5 }} />
                    </Box>
                    <Switch checked={rule.isActive} size="small" />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {rule.description}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Category:
                    </Typography>
                    <Chip label={rule.category} size="small" sx={{ ml: 1 }} />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Priority:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} color={i < rule.priority ? '#ffc107' : '#e0e0e0'} />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Modified: {rule.lastModified} by {rule.createdBy}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      {rule.exceptions.length > 0 && (
                        <Chip label={`${rule.exceptions.length} exceptions`} size="small" color="secondary" />
                      )}
                    </Box>
                    <Box>
                      <IconButton size="small" color="primary">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small" color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  const renderPreferenceWeights = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Preference Weights
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Adjust the importance of different factors in timetable generation
            </Typography>

            {weights.map((weight) => (
              <Box key={weight.id} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1">{weight.name}</Typography>
                  <Typography variant="h6" color="primary">
                    {(weight.currentWeight * 100).toFixed(0)}%
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {weight.description}
                </Typography>

                <Slider
                  value={weight.currentWeight}
                  min={weight.range.min}
                  max={weight.range.max}
                  step={0.1}
                  onChange={(_, newValue) => handleWeightChange(weight.id, newValue as number)}
                  marks={[
                    { value: weight.range.min, label: `${(weight.range.min * 100).toFixed(0)}%` },
                    { value: weight.defaultWeight, label: 'Default' },
                    { value: weight.range.max, label: `${(weight.range.max * 100).toFixed(0)}%` },
                  ]}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={weight.category} size="small" color="primary" variant="outlined" />
                  <Tooltip title={`Recommended: ${(weight.impactAnalysis.recommendedValue * 100).toFixed(0)}%`}>
                    <Button
                      size="small"
                      onClick={() => handleWeightChange(weight.id, weight.impactAnalysis.recommendedValue)}
                    >
                      Use Recommended
                    </Button>
                  </Tooltip>
                </Box>

                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Weighting Profiles
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Pre-configured weight combinations
            </Typography>

            {profiles.map((profile) => (
              <Card key={profile.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">{profile.name}</Typography>
                    {profile.isDefault && <Chip label="Default" size="small" color="primary" />}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {profile.description}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption">Success Rate</Typography>
                      <Typography variant="caption">{profile.successRate}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={profile.successRate}
                      color={profile.successRate > 80 ? 'success' : 'warning'}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Avg. Conflicts: {profile.averageConflicts}
                    </Typography>
                    <Button size="small">Apply</Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderConflictAnalysis = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Rule Conflicts Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Identify and resolve conflicts between timetabling rules
        </Typography>

        {conflicts.length === 0 ? (
          <Alert severity="success" icon={<CheckCircle />}>
            No rule conflicts detected. All rules are compatible.
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Conflict</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Affected Rules</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conflicts.map((conflict) => (
                  <TableRow key={conflict.id}>
                    <TableCell>
                      <Typography variant="subtitle2">{conflict.description}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {conflict.affectedEntities.subjects?.join(', ') ||
                          conflict.affectedEntities.teachers?.join(', ') ||
                          conflict.affectedEntities.rooms?.join(', ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={conflict.type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label={conflict.severity} color={getConflictColor(conflict.severity) as any} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{conflict.conflictingRules.join(', ')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={conflict.status}
                        color={conflict.status === 'resolved' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary">
                        <Visibility />
                      </IconButton>
                      {conflict.status === 'unresolved' && (
                        <IconButton size="small" color="warning">
                          <Settings />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>

      {conflicts.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resolution Suggestions
              </Typography>
              {conflicts
                .filter((c) => c.status === 'unresolved')
                .map((conflict) => (
                  <Accordion key={conflict.id}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Warning color="warning" style={{ marginRight: 8 }} />
                        <Typography variant="subtitle2">{conflict.description}</Typography>
                        <Chip
                          label={conflict.severity}
                          color={getConflictColor(conflict.severity) as any}
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Resolution options (recommended: {conflict.suggestedResolution}):
                      </Typography>
                      {conflict.resolutionOptions.map((option) => (
                        <Card key={option.id} variant="outlined" sx={{ mb: 2 }}>
                          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2">{option.description}</Typography>
                              <Chip
                                label={`${(option.confidence * 100).toFixed(0)}% confidence`}
                                size="small"
                                color={option.confidence > 0.8 ? 'success' : 'warning'}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {option.impact}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Chip label={option.action} size="small" variant="outlined" />
                              <Button
                                size="small"
                                variant={conflict.suggestedResolution === option.id ? 'contained' : 'outlined'}
                              >
                                Apply Resolution
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );

  const renderConstraintMatrix = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Subject Constraints
            </Typography>
            {mockData.subjectConstraints.map((constraint) => (
              <Accordion key={constraint.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">{constraint.subjectName}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Hard Rules
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Periods per week"
                            secondary={`${constraint.hardRules.minimumPeriodsPerWeek} - ${constraint.hardRules.maximumPeriodsPerWeek}`}
                          />
                        </ListItem>
                        {constraint.hardRules.requiredRoomType && (
                          <ListItem>
                            <ListItemText
                              primary="Required room type"
                              secondary={constraint.hardRules.requiredRoomType.join(', ')}
                            />
                          </ListItem>
                        )}
                        {constraint.hardRules.doublePeriodsRequired && (
                          <ListItem>
                            <ListItemText primary="Double periods" secondary="Required" />
                          </ListItem>
                        )}
                      </List>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>
                        Soft Rules
                      </Typography>
                      <List dense>
                        {constraint.softRules.preferredTimeslots && (
                          <ListItem>
                            <ListItemText
                              primary="Preferred timeslots"
                              secondary={constraint.softRules.preferredTimeslots.join(', ')}
                            />
                          </ListItem>
                        )}
                        {constraint.softRules.morningPreference && (
                          <ListItem>
                            <ListItemText primary="Morning preference" secondary="Yes" />
                          </ListItem>
                        )}
                      </List>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Teacher Constraints
            </Typography>
            {mockData.teacherConstraints.map((constraint) => (
              <Accordion key={constraint.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">{constraint.teacherName}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Hard Rules
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Max periods per day"
                            secondary={constraint.hardRules.maxPeriodsPerDay}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Max consecutive periods"
                            secondary={constraint.hardRules.maxConsecutivePeriods}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Contractual hours"
                            secondary={`${constraint.hardRules.contractualHours} per week`}
                          />
                        </ListItem>
                        {constraint.hardRules.unavailableDays.length > 0 && (
                          <ListItem>
                            <ListItemText
                              primary="Unavailable days"
                              secondary={constraint.hardRules.unavailableDays.join(', ')}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>
                        Preferences
                      </Typography>
                      <List dense>
                        {constraint.softRules.preferredDays && (
                          <ListItem>
                            <ListItemText
                              primary="Preferred days"
                              secondary={constraint.softRules.preferredDays.join(', ')}
                            />
                          </ListItem>
                        )}
                        <ListItem>
                          <ListItemText
                            primary="Workload distribution"
                            secondary={constraint.softRules.workloadDistribution}
                          />
                        </ListItem>
                        {constraint.teachingPreferences.yearGroupPreferences.length > 0 && (
                          <ListItem>
                            <ListItemText
                              primary="Preferred year groups"
                              secondary={constraint.teachingPreferences.yearGroupPreferences.join(', ')}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Timetable Rules & Preferences
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Define constraints, preferences, and weighting for optimal timetable generation
      </Typography>

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="rules management tabs">
            <Tab icon={<Rules />} label="Rule Management" />
            <Tab icon={<Preferences />} label="Preference Weights" />
            <Tab icon={<Conflicts />} label="Conflict Analysis" />
            <Tab icon={<Analytics />} label="Constraint Matrix" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {renderRuleManagement()}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderPreferenceWeights()}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {renderConflictAnalysis()}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {renderConstraintMatrix()}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Add Rule Dialog */}
      <Dialog open={showRuleDialog} onClose={() => setShowRuleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Rule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rule Name"
                value={newRule.name || ''}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rule Type</InputLabel>
                <Select value={newRule.type || ''} onChange={(e) => setNewRule({ ...newRule, type: e.target.value })}>
                  <MenuItem value="hard">Hard Rule</MenuItem>
                  <MenuItem value="soft">Soft Rule</MenuItem>
                  <MenuItem value="preference">Preference</MenuItem>
                  <MenuItem value="teaching-preference">Teaching Preference</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newRule.category || ''}
                  onChange={(e) => setNewRule({ ...newRule, category: e.target.value })}
                >
                  <MenuItem value="subject">Subject</MenuItem>
                  <MenuItem value="teacher">Teacher</MenuItem>
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="room">Room</MenuItem>
                  <MenuItem value="time">Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newRule.priority || ''}
                  onChange={(e) => setNewRule({ ...newRule, priority: e.target.value })}
                >
                  <MenuItem value={1}>1 - Lowest</MenuItem>
                  <MenuItem value={2}>2 - Low</MenuItem>
                  <MenuItem value={3}>3 - Medium</MenuItem>
                  <MenuItem value={4}>4 - High</MenuItem>
                  <MenuItem value={5}>5 - Highest</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newRule.description || ''}
                onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRuleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddRule}>
            Add Rule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimetableRulesPreferences;
